import React, { useState, useEffect } from 'react';
import { Building2, Upload } from 'lucide-react';
import { uploadFile } from '../../services/usersapi';
import { useNavigate } from 'react-router-dom';
import { useRbac } from '../../context';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../services/apiinterseptor';
import { getCurrentUserDetails } from '../../services/apiclient';

const CompanyOnboardingForm: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, user } = useRbac();
  const FORM_STORAGE_KEY = 'companyOnboardingFormData';
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [publicCompanyData, setPublicCompanyData] = useState<any>(null);

  // Initial form data with localStorage fallback
  const getInitialFormData = () => {
    try {
      const savedData = localStorage.getItem(FORM_STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        return parsedData;
      }
    } catch (error) {
      console.error('Error loading form data from localStorage:', error);
    }
    
    return {
      name: '',
      brandName: '',
      website: '',
      email: '',
      phone: '',
      gstNo: '',
      registeredAddress: '',
      operationalAddress: '',
      sameAsRegistered: false,
      logoUrl: '',
      agreeTermsAndConditions: false
    };
  };

  const [formData, setFormData] = useState<{
    name: string;
    brandName: string;
    website: string;
    email: string;
    phone: string;
    gstNo: string;
    registeredAddress: string;
    operationalAddress: string;
    sameAsRegistered: boolean;
    logoUrl: string;
    agreeTermsAndConditions: boolean;
  }>(getInitialFormData);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Save to localStorage whenever form data changes
  useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Error saving form data to localStorage:', error);
    }
  }, [formData]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  // Fetch public company data and pre-fill form
  useEffect(() => {
    const fetchPublicCompanyData = async () => {
      console.log('=== COMPANY ONBOARDING FORM - FETCH PUBLIC COMPANY DATA ===');
      console.log('1. Starting data fetch...');
      console.log('2. User ID:', user?._id);
      console.log('3. User object:', user);

      if (!user?._id) {
        console.log('❌ No user ID found, skipping data fetch');
        setIsLoadingData(false);
        return;
      }

      try {
        // Check if we already have saved data in localStorage
        const savedData = localStorage.getItem(FORM_STORAGE_KEY);
        console.log('4. Checking localStorage for saved data...');
        console.log('5. LocalStorage data exists:', !!savedData);
        
        if (savedData) {
          console.log('✓ Found saved data in localStorage, not overwriting');
          setIsLoadingData(false);
          return; // Don't overwrite user's saved progress
        }

        console.log('6. No localStorage data, fetching from API...');
        
        // Fetch all public companies and find the one with matching createdUserId
        const response = await axiosInstance.get('/publicCompanies', {
          params: { page: 1, limit: 100 } // Fetch enough to find the match
        });

        console.log('7. API Response received');
        console.log('8. Total companies fetched:', response?.data?.jobs?.length || 0);
        console.log('9. First few companies:', response?.data?.jobs?.slice(0, 3));

        if (response?.data?.jobs && response.data.jobs.length > 0) {
          console.log('10. Searching for company with createdUserId matching:', user._id);
          
          // Log all createdUserIds for debugging
          const allCreatedUserIds = response.data.jobs
            .filter((c: any) => c.createdUserId)
            .map((c: any) => ({
              companyName: c.companyName,
              createdUserId: c.createdUserId
            }));
          console.log('11. All companies with createdUserId:', allCreatedUserIds);

          // Find the public company where createdUserId matches current user's ID
          const userPublicCompany = response.data.jobs.find(
            (company: any) => company.createdUserId === user._id
          );

          console.log('12. Matching company found:', !!userPublicCompany);

          if (userPublicCompany) {
            console.log('✓ Found matching public company data:');
            console.log('   - Company Name:', userPublicCompany.companyName);
            console.log('   - Brand Name:', userPublicCompany.brandName);
            console.log('   - HR Name:', userPublicCompany.hrName);
            console.log('   - HR Email:', userPublicCompany.hrEmail);
            console.log('   - HR Contact:', userPublicCompany.hrContact);
            console.log('   - City:', userPublicCompany.city);
            console.log('   - Logo URL:', userPublicCompany.logoUrl);
            console.log('   - Full data:', userPublicCompany);
            
            // Store public company data for later use (SPOC details in submission)
            setPublicCompanyData(userPublicCompany);
            console.log('13. Stored public company data for SPOC details');
            
            // Construct full address from public company data
            const fullAddress = userPublicCompany.streetAddress && userPublicCompany.city
              ? `${userPublicCompany.streetAddress}, ${userPublicCompany.area || ''}, ${userPublicCompany.city} - ${userPublicCompany.pincode || ''}`
              : '';
            
            console.log('14. Constructed address:', fullAddress);

            // Pre-fill form with public company data
            const formDataToSet = {
              name: userPublicCompany.companyName || '',
              brandName: userPublicCompany.brandName || userPublicCompany.companyName || '',
              website: userPublicCompany.website || '',
              email: userPublicCompany.hrEmail || user.email || '',
              phone: userPublicCompany.hrContact || user.phone || '',
              gstNo: '',
              registeredAddress: fullAddress,
              operationalAddress: fullAddress,
              sameAsRegistered: true,
              logoUrl: userPublicCompany.logoUrl || '',
              agreeTermsAndConditions: false
            };

            console.log('15. Form data to be set:', formDataToSet);
            setFormData(formDataToSet);

            // Set logo preview if logoUrl exists
            if (userPublicCompany.logoUrl) {
              console.log('16. Setting logo preview:', userPublicCompany.logoUrl);
              setLogoPreview(userPublicCompany.logoUrl);
            }

            console.log('✓ Form pre-filled successfully!');
            toast.success('Form pre-filled with your company details');
          } else {
            console.log('❌ No matching company found for user ID:', user._id);
            console.log('   Tip: Check if createdUserId was set during company approval');
          }
        } else {
          console.log('❌ No companies found in API response');
        }
      } catch (error: any) {
        console.error('❌ ERROR fetching public company data:');
        console.error('   Error message:', error.message);
        console.error('   Error response:', error.response?.data);
        console.error('   Full error:', error);
        // Don't show error toast, just continue with empty form
      } finally {
        console.log('16. Setting loading state to false');
        setIsLoadingData(false);
        console.log('=== END FETCH PUBLIC COMPANY DATA ===\n');
      }
    };

    fetchPublicCompanyData();
  }, [user?._id]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'sameAsRegistered') {
      setFormData(prev => ({
        ...prev,
        sameAsRegistered: checked,
        operationalAddress: checked ? prev.registeredAddress : prev.operationalAddress
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));

      // If registered address changes and sameAsRegistered is true, update operational address
      if (name === 'registeredAddress' && formData.sameAsRegistered) {
        setFormData(prev => ({
          ...prev,
          operationalAddress: value
        }));
      }
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle logo upload
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      e.target.value = ''; // Reset input
      return;
    }

    // Clean up old preview URL if it exists
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoFile(file);
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
  };

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.registeredAddress.trim()) newErrors.registeredAddress = 'Registered address is required';
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate phone format (10 digits)
    const phoneRegex = /^\d{10}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    // Validate GST format (15 characters) - only if provided
    if (formData.gstNo && formData.gstNo.trim() && formData.gstNo.length !== 15) {
      newErrors.gstNo = 'GST number must be 15 characters';
    }

    if (!formData.agreeTermsAndConditions) {
      newErrors.terms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setIsSubmitting(true);

    try {
      let logoUrl = formData.logoUrl;

      // Upload logo if a new file is selected
      if (logoFile) {
        const folderPath = `companies/logos`;
        const uploadResponse = await uploadFile(logoFile, folderPath);
        logoUrl = uploadResponse.fileUrl;
      }

      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('1. Public Company Data available:', !!publicCompanyData);
      console.log('2. Public Company Data:', publicCompanyData);
      console.log('3. User data:', user);
      console.log('4. Form data:', formData);

      // Prepare SPOC details from PublicCompany data
      // If publicCompanyData is available, use HR details from it
      // Otherwise, use form data as fallback
      let spocDetails = [];
      
      if (publicCompanyData) {
        console.log('5. Using SPOC details from PublicCompany data');
        console.log('   - HR Name:', publicCompanyData.hrName);
        console.log('   - HR Email:', publicCompanyData.hrEmail);
        console.log('   - HR Contact:', publicCompanyData.hrContact);
        console.log('   - City (location):', publicCompanyData.city);
        
        spocDetails = [{
          name: publicCompanyData.hrName || 'N/A',
          email: publicCompanyData.hrEmail || '',
          phone: publicCompanyData.hrContact || '',
          location: publicCompanyData.city || 'Not specified' // Fetch city as location
        }];
      } else {
        console.log('5. ⚠️ PublicCompany data not found, using form data as SPOC fallback');
        
        // Try to extract city from registered address as fallback
        let cityFromAddress = 'Not specified';
        if (formData.registeredAddress) {
          // Try to extract city from address (assuming format: "street, area, city - pincode")
          const addressParts = formData.registeredAddress.split(',');
          if (addressParts.length >= 2) {
            const lastPart = addressParts[addressParts.length - 1].trim();
            const cityPart = lastPart.split('-')[0].trim();
            if (cityPart) {
              cityFromAddress = cityPart;
            }
          }
        }
        
        console.log('   - Extracted location from address:', cityFromAddress);
        
        // Fallback: Use the form data as SPOC details
        spocDetails = [{
          name: user?.username || 'N/A',
          email: formData.email || user?.email || '',
          phone: formData.phone || user?.phone || '',
          location: cityFromAddress // Extract from address or use default
        }];
      }

      console.log('6. Final SPOC Details:', spocDetails);
      console.log('7. SPOC count:', spocDetails.length);
      console.log('8. SPOC location:', spocDetails[0]?.location);

      // Prepare submission data
      const submissionData = {
        name: formData.name,
        brandName: formData.brandName || formData.name,
        website: formData.website,
        email: formData.email,
        phone: formData.phone,
        gstNo: formData.gstNo,
        registeredAddress: formData.registeredAddress,
        operationalAddress: formData.sameAsRegistered ? formData.registeredAddress : formData.operationalAddress,
        logoUrl: logoUrl,
        spocDetails: spocDetails, // Use SPOC details from PublicCompany data
        onboardedByUserId: user?._id || null,
        referredByFbdeUserId: null,
        onboardingFranchiseId: null
      };

      console.log('Full submission data:', submissionData);

      // Submit to backend
      const response = await axiosInstance.post('/companies/create', submissionData);

      if (response.data) {
        toast.success('Company onboarding completed successfully!');
        
        // Clean up blob URL
        if (logoPreview && logoPreview.startsWith('blob:')) {
          URL.revokeObjectURL(logoPreview);
        }

        // Clear localStorage
        localStorage.removeItem(FORM_STORAGE_KEY);

        // Fetch updated user data from backend to sync context with database
        try {
          console.log('📥 Fetching updated user data after company onboarding...');
          const updatedUserResponse = await getCurrentUserDetails();
          
          if (updatedUserResponse?.data?.user && setUser) {
            console.log('✅ User data updated from backend:', updatedUserResponse.data.user);
            console.log('   - isOnboarded:', updatedUserResponse.data.user.isOnboarded);
            console.log('   - companyId:', updatedUserResponse.data.user.companyId);
            setUser(updatedUserResponse.data.user as any);
          }
        } catch (error) {
          console.error('⚠️ Failed to fetch updated user data:', error);
          // Fallback: Update context with what we know
          if (setUser && user) {
            setUser({
              ...user,
              isOnboarded: true,
              companyId: response.data.data.companyId
            } as any);
          }
        }

        // Redirect to dashboard
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit company onboarding';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-orange-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600 text-lg">Loading your company details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Building2 className="w-12 h-12 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Onboarding</h1>
          <p className="text-gray-600">Complete your company profile to get started</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
          {/* Company Basic Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-orange-600" />
              Company Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter company name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Name
                </label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter brand name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="company@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST Number
                </label>
                <input
                  type="text"
                  name="gstNo"
                  value={formData.gstNo}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="15-character GST number (optional)"
                  maxLength={15}
                />
                {errors.gstNo && <p className="text-red-500 text-sm mt-1">{errors.gstNo}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="https://www.example.com"
                />
              </div>
            </div>
          </div>

          {/* Address Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Address Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registered Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="registeredAddress"
                  value={formData.registeredAddress}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter complete registered address"
                />
                {errors.registeredAddress && <p className="text-red-500 text-sm mt-1">{errors.registeredAddress}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="sameAsRegistered"
                  checked={formData.sameAsRegistered}
                  onChange={handleChange}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Operational address is same as registered address
                </label>
              </div>

              {!formData.sameAsRegistered && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Operational Address
                  </label>
                  <textarea
                    name="operationalAddress"
                    value={formData.operationalAddress}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter operational address"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Logo Upload */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Logo</h2>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="cursor-pointer">
                {logoPreview || formData.logoUrl ? (
                  <div className="space-y-4">
                    <img
                      src={logoPreview || formData.logoUrl}
                      alt="Company Logo"
                      className="mx-auto h-32 w-32 object-contain rounded-lg"
                    />
                    <p className="text-sm text-gray-600">Click to change logo</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Click to upload company logo</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="mb-8">
            <div className="flex items-start">
              <input
                type="checkbox"
                name="agreeTermsAndConditions"
                checked={formData.agreeTermsAndConditions}
                onChange={handleChange}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 mt-1"
              />
              <label className="ml-2 text-sm text-gray-700">
                I agree to the <a href="#" className="text-orange-600 hover:underline">Terms and Conditions</a> and confirm that all information provided is accurate.
              </label>
            </div>
            {errors.terms && <p className="text-red-500 text-sm mt-1">{errors.terms}</p>}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Complete Onboarding'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyOnboardingForm;
